import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, Download, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 5;
const POLL_MS = 2000;

const STATUS_CONFIG = {
  checking: {
    icon: <Loader2 className="h-10 w-10 animate-spin text-[#2f7fff]" />,
    title: "Checking your payment...",
    text: "Hold tight, this only takes a few seconds.",
  },
  success: {
    icon: <CheckCircle2 className="h-10 w-10 text-emerald-400" />,
    title: "Payment successful!",
    text: "Your PDF is ready — download it below. We've also emailed you a copy (check spam if you don't see it).",
  },
  expired: {
    icon: <XCircle className="h-10 w-10 text-red-400" />,
    title: "Session expired",
    text: "Your payment session expired. Please try again.",
  },
  timeout: {
    icon: <XCircle className="h-10 w-10 text-amber-400" />,
    title: "Status check timed out",
    text: "Please check your email for confirmation of your purchase.",
  },
  error: {
    icon: <XCircle className="h-10 w-10 text-red-400" />,
    title: "Something went wrong",
    text: "We couldn't verify your payment. Please contact support.",
  },
};

export const PaymentStatus = () => {
  const [state, setState] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const attempts = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) return undefined;
    let cancelled = false;
    setState("checking");

    const poll = async () => {
      if (attempts.current >= MAX_ATTEMPTS) {
        if (!cancelled) setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setDownloads(data.downloads || []);
          setState("success");
        } else if (data.status === "expired") {
          setState("expired");
        } else {
          timer.current = setTimeout(poll, POLL_MS);
        }
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, []);

  if (!state) return null;
  const config = STATUS_CONFIG[state];

  const close = () => {
    window.history.replaceState({}, "", window.location.pathname);
    setState(null);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="payment-status-modal">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0b0d10] p-8 text-center">
        <div className="flex justify-center">{config.icon}</div>
        <h3 className="mt-5 text-lg font-semibold text-white" data-testid="payment-status-title">{config.title}</h3>
        <p className="mt-2 text-sm text-zinc-400">{config.text}</p>
        {state === "success" && downloads.length > 0 && (
          <div className="mt-5 space-y-2">
            {downloads.map((d) => (
              <a
                key={d.url}
                href={d.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 px-6 py-3 text-sm font-semibold text-white transition-colors"
                data-testid={`download-button-${d.url.split("/").pop()}`}
              >
                <Download className="h-4 w-4" /> {d.label}
              </a>
            ))}
          </div>
        )}
        {state !== "checking" && (
          <button
            onClick={close}
            className="mt-6 rounded-full bg-[#2f7fff] hover:bg-[#4b91ff] px-6 py-2.5 text-sm font-semibold text-white transition-colors"
            data-testid="payment-status-close-button"
          >
            Continue
          </button>
        )}
      </div>
    </div>
  );
};
